import React, { useMemo, useCallback, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'

import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepButton from '@material-ui/core/StepButton'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import CardActionArea from '@material-ui/core/CardActionArea'
import CardMedia from '@material-ui/core/CardMedia'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import Window from '@nocode-works/template/components/dialog/Window'
import Form from '@nocode-works/template/components/form/Form'

import websiteSelectors from '@nocode-works/template/store/selectors/website'
import templateSelectors from 'store/selectors/template'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  nameField: {
    width: '100%',
  },
  card: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    height: '100%',
  },
  media: {
    height: 260,
    border: '1px solid #ccc',
  },
  divider: {
    height: theme.spacing(4),
  },
  content: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  },
  cardActionsButton: {
    flexGrow: 0,
  },
  cardActionsFiller: {
    flexGrow: 1,
  },
  rightButton: {
    marginLeft: theme.spacing(1),
  },
  stepper: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  nextButton: {
    marginTop: theme.spacing(2),
  }
}))

const CreateWebsiteDialogName = ({
  value,
  isValid,
  nameError,
  onSetValue,
}) => {
  const classes = useStyles()
  return (
    <TextField
      className={ classes.nameField }
      label="Website Name"
      value={ value }
      error={ isValid ? false : true }
      onChange={ e => onSetValue(e.target.value) }
      helperText={ nameError ? nameError : `enter the name of your website` }
    />
  )
}

const CreateWebsiteDialogTemplate = ({
  templates,
  onSetValue,
}) => {
  const classes = useStyles()
  return (
    <Grid container spacing={ 2 }>
      {
        templates.map((template, i) => {
          const {
            nocode = {},
            screenshot,
          } = template.version.meta
          const {
            name,
            description,
            exampleUrl,
          } = nocode

          return (
            <Grid item key={ i } xs={ 12 } sm={ 4 }>
              <Card
                className={classes.card}
              >
                <CardActionArea
                  onClick={ () => onSetValue(template) }
                >
                  <CardMedia
                    className={ classes.media }
                    image={ screenshot.url }
                    title={ name }
                  />
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="h2">
                      { name }
                    </Typography>
                    <Typography variant="body2" color="textSecondary" component="p">
                      { description }
                    </Typography>
                  </CardContent>
                </CardActionArea>
                <CardActions>
                  <div className={ classes.cardActions }>
                    {
                      exampleUrl && (
                        <div className={ classes.cardActionsButton }>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={ () => window.open(exampleUrl) }
                          >
                            Example
                          </Button>
                        </div>
                      )
                    }
                    <div className={ classes.cardActionsFiller }>
                    </div>
                    <div className={ classes.cardActionsButton }>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={ () => onSetValue(template) }
                      >
                        Choose
                      </Button>
                    </div>
                  </div>
                </CardActions>
              </Card>
            </Grid>
          )
        })
      }
    </Grid>
  )
}

const CreateWebsiteInitialOptions = ({
  settings,
  onSubmitForm,
}) => {

  const classes = useStyles()

  const schema = useMemo(() => {
    if(!settings) return []
    const tabs = settings.tabs || []
    const initialOptions = settings.initialOptions || []
    const initialSchema = tabs
      .reduce((all, tab) => {
        return all.concat(tab.schema)
      }, [])
      .filter(schemaItem => initialOptions.indexOf(schemaItem.id) >= 0)
    return initialSchema
  }, [
    settings,
  ])

  if(!settings) return null

  return (
    <Form
      schema={ schema }
      initialValues={ {} }
      onSubmit={ onSubmitForm }
    >
      {
        ({
          isValid,
          onSubmit,
        }) => {
          return (
            <div>
              <Divider className={ classes.divider } />
              <Button
                color="primary"
                variant="contained"
                disabled={ isValid ? false : true }
                onClick={ onSubmit }
              >
                Create Website
              </Button>
            </div>
          )
        }
      }
    </Form>
  )
}

const STEPS = [{
  title: 'Name your website',
  description: 'Choose a name that reflects what this website is for...',
}, {
  title: 'Select Template',
  description: 'Templates have different features - choose the template that matches what you want to build...',
}, {
  title: 'Options',
  description: 'Choose the options you want for your website - you can always change these later...',
}]

const getNameValidateError = name => {
  if(!name) return `please enter a name`
  if(name.length > 100) return `Maximum length of a website name is 100 characters`
  return null
}

const CreateWebsiteDialog = ({
  onSubmit,
  onCancel,
}) => {

  const classes = useStyles()
  const websites = useSelector(websiteSelectors.websites)
  const templates = useSelector(templateSelectors.allTemplates)

  const [ step, setStep ] = useState(0)
  const [ name, setName ] = useState(websites.length > 0 ? `New website` : `My first nocode website`)
  const [ template, setTemplate ] = useState(null)

  const settings = template ?
    template.version.meta.settings :
    {}

  const defaultLayout = useMemo(() => {
    if(!settings) return ''
    if(!settings.layout || Object.keys(settings.layout).length <= 0) return ''
    if(settings.defaultLayout) return settings.defaultLayout
    return Object.keys(settings.layout)[0]
  }, [
    settings,
  ])

  const nameError = getNameValidateError(name)

  const isNameValid = nameError ? false : true
  
  const onSetTemplate = useCallback(template => {
    setTemplate(template)
    setStep(step + 1)
  }, [
    step,
  ])

  const onSetInitialOptions = useCallback((options) => {
    onSubmit({
      name,
      template,
      options,
      layout: defaultLayout,
    })
  }, [
    name,
    template,
    defaultLayout,
  ])

  let content = null

  if(step == 0) {
    content = (
      <div>
        <CreateWebsiteDialogName
          value={ name }
          isValid={ isNameValid }
          nameError={ nameError }
          onSetValue={ setName }
        />
        <Button
          className={ classes.nextButton }
          variant="contained"
          color="primary"
          size="small"
          disabled={ isNameValid ? false : true }
          onClick={ () => setStep(step + 1)}
        >
          Next
        </Button>
      </div>
    )
  }
  else if(step == 1) {
    content = (
      <CreateWebsiteDialogTemplate
        templates={ templates }
        onSetValue={ onSetTemplate }
      />
    )
  }
  else if(step == 2) {
    content = (
      <CreateWebsiteInitialOptions
        settings={ settings }
        onSubmitForm={ onSetInitialOptions }
      />
    )
  }

  const stepper = (
    <Stepper activeStep={step} className={ classes.stepper }>
      {STEPS.map((stepInfo, index) => {
        return (
          <Step
            key={ index }
            completed={ step > index }
          >
            <StepButton
              completed={ step > index }
            >
              { stepInfo.title }
            </StepButton>
          </Step>
        )
      })}
    </Stepper>
  )

  const leftButtons = (
    <div>
      {
        step > 0 && (
          <Button
            variant="contained"
            onClick={ () => setStep(step - 1)}
          >
            Back
          </Button>
        )
      }
    </div>
  )

  const rightButtons = (
    <div>
      <Button
        className={ classes.rightButton }
        variant="contained"
        onClick={ onCancel }
      >
        Cancel
      </Button>
    </div>
  )

  const stepInfo = STEPS[step]

  return (
    <Window
      open
      fullHeight
      title={ stepper }
      size="lg"
      theme={{
        content: classes.content,
      }}
      leftButtons={ leftButtons }
      rightButtons={ rightButtons }
      onCancel={ onCancel }
    >
      <Typography gutterBottom variant="h6">
        { stepInfo.title }
      </Typography>
      <Typography gutterBottom variant="body2">
        { stepInfo.description }
      </Typography>
      <Divider className={ classes.divider } />
      { content }
    </Window>
  )
}

export default CreateWebsiteDialog